import { Routes } from "@angular/router";
import { LandingPageComponent } from "./landing-page/landing-page.component";
import { AboutUsComponent } from "./about-us/about-us.component";
import { BlogComponent } from "./blog/blog.component";
import { BlogDetailComponent } from "./blog/components/blog-detail/blog-detail.component";
import { CoursesComponent } from "./courses/courses.component";

import { PurchaseOrderComponent } from "./purchase-order/purchase-order.component";

import { CourseDetailComponent } from "./courses/course-detail/course-detail.component";
import { AuthGuard } from "./auth/services/auth.guard";
import { PurchaseOrderDetailComponent } from "./purchase-order/purchase-order-detail/purchase-order-detail.component";
import { CourseCategoriesComponent } from "./courses/course-categories/course-categories.component";
import { StudentWatchComponent } from "./online-courses/student-watch/student-watch.component";
import { OnlineCoursesComponent } from "./online-courses/online-courses.component";
import { DashboardStudentCoursesComponent } from "./online-courses/dashboard/dashboard.component";
import { ComingSoonComponent } from "./coming-soon/coming-soon.component";
import { ProfileComponent } from "./profile/profile.component";
import { MonitorComponent } from "./profile/monitor/monitor.component";
import { AccountComponent } from "./profile/account/account.component";
import { UserParentComponent } from "./profile/user-parent/user-parent.component";
import { UserPasswordComponent } from "./profile/user-password/user-password.component";
import { QuizRecordComponent } from "./profile/monitor/quiz-record/quiz-record.component";
import { QuizListComponent } from "./quiz/quiz-list/quiz-list.component";
import { QuizTakeComponent } from "./quiz/quiz-take/quiz-take.component";
import { QuizResultComponent } from "./quiz/quiz-result/quiz-result.component";
import { MySkillsComponent } from "./quiz/my-skills/my-skills.component";
import { CourseProgressComponent } from "./profile/monitor/course-progress/course-progress.component";
import { StudyRecordComponent } from "./profile/monitor/study-record/study-record.component";
import { ParentCoursesComponent } from "./profile/parent-courses/parent-courses.component";
import { RoleID } from "src/utils/enum/role.enum";
import { Permission } from "src/utils/enum/permission.enum";
import { DefaultComponentComponent } from "./default-component/default-component.component";
import { ManualComponent } from "./manual/manual.component";
import { DEMO } from "src/app/config/brand.config";
import { PrivacyPolicyComponent } from "./legal/privacy-policy/privacy-policy.component";
import { TermsConditionsComponent } from "./legal/terms-conditions/terms-conditions.component";
import { CookiesPolicyComponent } from "./legal/cookies-policy/cookies-policy.component";
import { PaymentPromotionPolicyComponent } from "./legal/payment-promotion-policy/payment-promotion-policy.component";

const Routing: Routes = [
  {
    path: "home",
    title: "MENU.HOME",
    component: LandingPageComponent,
    data: { toolbar: false },
  },
  {
    path: "reset-password",
    // title: "MENU.HOME",
    component: LandingPageComponent,
    data: { toolbar: false, authDialog: "reset" },
  },
  // Sign-in and sign-up are dialogs, not pages — but they are the two links
  // most likely to be pasted into a message or written into a manual, and
  // before this they answered 404. They open the dialog over the landing page.
  {
    path: "auth/login",
    component: LandingPageComponent,
    data: { toolbar: false, authDialog: "login" },
  },
  {
    path: "auth/registration",
    component: LandingPageComponent,
    data: { toolbar: false, authDialog: "register" },
  },
  {
    path: "auth/forgot-password",
    component: LandingPageComponent,
    data: { toolbar: false, authDialog: "forgot" },
  },
  {
    path: "courses",
    title: "MENU.COURSES",
    data: { toolbar: true },
    children: [
      {
        path: "",
        component: CoursesComponent,
        title: "MENU.COURSES",
        data: {
          toolbar: true,
        },
      },
      {
        path: ":course_id",
        component: CourseDetailComponent,
        data: {
          toolbar: true,
        },
      },
      {
        path: "category/:category_id",
        component: CourseCategoriesComponent,
        data: {
          toolbar: true,
        },
      },
      // `category/:category_id/course` used to live here, reached only by
      // appending `?course=N` — the bare path carried no course id at all, so a
      // trimmed URL, a shared link or a bookmark landed on skeletons that never
      // resolved. A course has one canonical address, `/courses/:course_id`,
      // and the category list now links to that like everything else does.
    ],
  },
  {
    path: "blogs",
    data: {
      toolbar: true,
    },
    children: [
      {
        path: "",
        component: BlogComponent,
        data: {
          toolbar: false,
        },
      },
      {
        path: ":blog_id",
        component: BlogDetailComponent,
        data: {
          toolbar: false,
        },
      },
    ],
  },

  {
    // Two routes, one component. The menu offers "read" and "watch" as separate
    // destinations because that is how people ask for help — and a link someone
    // pastes into a chat should open in the mode they meant.
    //
    // Demo-only: the manual describes the demo build. `canMatch` returning false
    // in a customer build makes the router skip these and fall through to `**`,
    // so `/manual` answers 404 there instead of rendering.
    path: "manual",
    title: "MENU.MANUAL",
    component: ManualComponent,
    canMatch: [() => DEMO.enabled],
    data: { toolbar: false },
  },
  {
    path: "manual/video",
    title: "MENU.MANUAL_VIDEO",
    component: ManualComponent,
    canMatch: [() => DEMO.enabled],
    data: { toolbar: false, mode: "video" },
  },
  {
    path: "about-us",
    component: AboutUsComponent,
    data: { toolbar: false },
  },
  // The policy pages. Public, no toolbar — they are linked from the footer on
  // every page and occasionally pasted into a chat, so each has its own stable
  // address rather than living behind a tab on one "legal" route.
  {
    path: "privacy-policy",
    title: "LEGAL.PRIVACY.TITLE",
    component: PrivacyPolicyComponent,
    data: { toolbar: false },
  },
  {
    path: "terms-conditions",
    title: "LEGAL.TERMS.TITLE",
    component: TermsConditionsComponent,
    data: { toolbar: false },
  },
  {
    path: "cookies-policy",
    title: "LEGAL.COOKIES.TITLE",
    component: CookiesPolicyComponent,
    data: { toolbar: false },
  },
  {
    path: "payment-promotion-policy",
    title: "LEGAL.PAYMENT.TITLE",
    component: PaymentPromotionPolicyComponent,
    data: { toolbar: false },
  },
  {
    path: "online-class",
    canActivate: [AuthGuard],
    title: "MENU.ONLINE_CLASS",
    component: OnlineCoursesComponent,

    data: {
      toolbar: true,
      roles: [RoleID.MEMBER],
      permissions: [Permission.ENTER_COURSE],
    },
    children: [
      {
        path: "",
        component: DashboardStudentCoursesComponent,
        data: {
          toolbar: true,
        },
      },
      {
        path: ":id/watch",
        title: "MENU.COURSES",
        component: StudentWatchComponent,
        data: { toolbar: true },
      },
      //TODO wait for v2.1 feature

      {
        // Quiz routes. `result/:attempt_id` is declared BEFORE `:quiz_id` — the
        // router matches in order, so the other way round "result" is swallowed
        // as a quiz id and the result page never opens.
        path: "quiz",
        title: "QUIZ.TITLE",
        component: QuizListComponent,
        canMatch: [() => DEMO.enabled],
        data: { toolbar: true },
      },
      {
        // Skills sat three levels deep under profile -> monitor -> skills, and
        // the only ways in were a small button on the quiz list and a link at
        // the bottom of a result page. It is the answer the whole engine
        // exists to produce; it belongs next to the practice it explains.
        path: "skills",
        title: "QUIZ.MY_SKILLS",
        component: MySkillsComponent,
        canMatch: [() => DEMO.enabled],
        data: { toolbar: true },
      },
      {
        path: "quiz/result/:attempt_id",
        title: "QUIZ.RESULT",
        component: QuizResultComponent,
        canMatch: [() => DEMO.enabled],
        data: { toolbar: true },
      },
      {
        path: "quiz/:quiz_id",
        title: "QUIZ.TITLE",
        component: QuizTakeComponent,
        canMatch: [() => DEMO.enabled],
        data: { toolbar: true },
      },
    ],
  },
  {
    path: "profile",
    canActivate: [AuthGuard],
    component: ProfileComponent,
    data: { toolbar: false },
    children: [
      // {
      //   path: "",
      //   component: ProfileComponent,
      //   title: "PURCHASE_ORDER.TITLE",
      //   data: {
      //     toolbar: true,
      //   },
      // },
      {
        path: "",
        redirectTo: "monitor",
        pathMatch: "full",
      },
      // {
      //   path: "dashboard",
      //   component: StudentDashboardComponent,
      // },
      {
        path: "monitor",
        component: MonitorComponent,
        title: "MENU.MONITOR",
        data: { toolbar: true },
        children: [
          {
            path: "",
            redirectTo: "progress",
            pathMatch: "full",
          },
          {
            // The first question anyone asks about someone else's studying is
            // "what are they taking, and how far in are they" — which is where
            // the 2021 build opened the parent's area too.
            path: "progress",
            component: CourseProgressComponent,
            title: "MENU.MONITOR",
            data: { toolbar: true },
          },
          {
            path: "study-record",
            component: StudyRecordComponent,
            title: "MENU.PROFILE",
            data: { toolbar: true },
          },
          //TODO wait for v2.1 feature
          {
            path: "quiz-record",
            component: QuizRecordComponent,
            title: "MENU.PROFILE",
            canMatch: [() => DEMO.enabled],
            data: { toolbar: true },
          },
          {
            path: "skills",
            component: MySkillsComponent,
            title: "QUIZ.MY_SKILLS",
            canMatch: [() => DEMO.enabled],
            data: { toolbar: true },
          },
        ],
      },
      {
        path: "account",
        component: AccountComponent,
        title: "MENU.PROFILE",
        data: { toolbar: true },
      },
      {
        path: "parent",
        component: UserParentComponent,
        title: "MENU.PARENT_MENU",
        data: { toolbar: true },
      },
      {
        path: "student",
        component: UserParentComponent,
        title: "MENU.PARENT_MENU",
        data: { toolbar: true },
      },
      {
        path: "password",
        component: UserPasswordComponent,
        title: "MENU.PASSWORD",
        data: { toolbar: true },
      },
      {
        path: "parent-courses",
        component: ParentCoursesComponent,
        title: "MENU.PASSWORD",
        data: {
          toolbar: true,
          roles: [RoleID.PARENT],
          permissions: [Permission.ASSIGN_COURSE],
        },
      },
    ],
  },
  {
    path: "purchase-order",
    canActivate: [AuthGuard],
    title: "PURCHASE_ORDER.TITLE",
    data: { toolbar: true },
    children: [
      {
        path: "",
        component: PurchaseOrderComponent,
        title: "PURCHASE_ORDER.TITLE",
        data: {
          toolbar: true,
        },
      },
      {
        path: ":id",
        component: PurchaseOrderDetailComponent,
        title: "PURCHASE_ORDER.ORDER_DETAILS",
        data: {
          toolbar: true,
        },
      },
    ],
  },
  {
    path: "",
    redirectTo: "/home",
    pathMatch: "full",
  },
  {
    // A mistyped URL used to become the homepage silently, so a broken link
    // looked like it had worked and nobody could tell what they had asked for
    // was gone. The 404 page says so and offers both ways out.
    path: "**",
    redirectTo: "error/404",
  },
];

export { Routing };
